/* @flow */
import React, { useContext } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';

// Components
import { Price } from '../Price';
import Title from '../Title';
import CreatedBy from './CreatedBy';
import OfferedBy from './OfferedBy';
import InternalSellerName from './InternalSellerName';

// Helpers
import get from 'lodash.get';
import { TileContext } from '../helpers/TileContext';

import { CREATOR_UNKNOWN } from '../constants';

import type { Info_item as InfoItemProp } from './__generated__/Info_item.graphql';
import type { Info_seller as InfoSellerProp } from './__generated__/Info_seller.graphql';
import type { Info_viewer as InfoViewerProp } from './__generated__/Info_viewer.graphql';

// Styles
import styles from './styles.scss';
import { MoreOptionsBadge } from '../Badges/MoreOptions';
import { ShippingBadge } from '../Badges/Shipping';

const CREATOR_ATTRIBUTED_BY = 'BY';
const CREATOR_ATTRIBUTED_BY_AND_DOCUMENTED = 'BY_AND_DOCUMENTED';

const CREATOR_ATTRIBUTION_LIST = [CREATOR_ATTRIBUTED_BY, CREATOR_ATTRIBUTED_BY_AND_DOCUMENTED];

type InfoComponentProps = {|
    currency: string,
    disableLinks?: boolean,
    item: InfoItemProp,
    showInternalSellerName?: boolean,
    showMoreOptionsBadge: boolean,
    showByLine?: boolean,
    showSellerName?: boolean,
    showCreatorName?: boolean,
    showPrice?: boolean,
    showTitle?: boolean,
    seller: ?InfoSellerProp,
    onClick?: () => void,
    moreOptionsTopMargin?: boolean,
    viewer: InfoViewerProp,
    priceBold: boolean,
    titleOneLine: boolean,
    titleClassName?: string,
|};

export function InfoComponent(props: InfoComponentProps) {
    const { imageSize } = useContext(TileContext);

    const creationDate = get(props.item, 'classification.creationDate') || null;
    const creatorName = get(props.item, 'creators[0].creator.displayName') || null;
    const pdpUrl = get(props.item, 'localizedPdpUrl');
    const title = get(props.item, 'title');
    const isArt = get(props.item, 'vertical') === 'art';
    const showMoreOptionsBadge = get(props, 'showMoreOptionsBadge');

    // Seller Info
    const sellerName = get(props.seller, 'sellerProfile.company');
    const shouldShowSellerName = !props.showCreatorName && props.showSellerName && !!sellerName;
    const shouldShowInternalSellerName = props.showInternalSellerName && !!sellerName;

    // Creator Attribution
    const creators = get(props.item, 'creators');
    const attribution =
        creators && creators.find(el => CREATOR_ATTRIBUTION_LIST.includes(el.attribution));
    const attributionCreatorName = get(attribution, 'creator.displayName');
    const shouldShowCreatorName =
        props.showCreatorName &&
        !!attributionCreatorName &&
        attributionCreatorName.toLowerCase() !== CREATOR_UNKNOWN;
    const { titleClassName } = props;

    return (
        <React.Fragment>
            {shouldShowInternalSellerName && <InternalSellerName sellerName={sellerName} />}
            <div
                className={classnames(styles.titleWrapper, {
                    [styles.hasByLine]: props.showByLine,
                    [styles.titleOneLine]: props.titleOneLine,
                })}
            >
                {props.showTitle && (
                    <Title
                        creationDate={creationDate}
                        creatorName={creatorName}
                        disableLinks={props.disableLinks}
                        isArt={isArt}
                        pdpUrl={pdpUrl}
                        title={title}
                        onClick={props.onClick}
                        titleOneLine={props.titleOneLine}
                        titleClassName={titleClassName}
                    />
                )}
                {props.showByLine && (
                    <div className={classnames(styles.byline, styles[imageSize])}>
                        {shouldShowCreatorName && (
                            <CreatedBy creatorName={attributionCreatorName} />
                        )}
                        {shouldShowSellerName && <OfferedBy sellerName={sellerName} />}
                    </div>
                )}
            </div>
            {props.showPrice && (
                <Price
                    currency={props.currency}
                    item={(props.item: Object)}
                    priceBold={props.priceBold}
                />
            )}
            <ShippingBadge item={(props.item: Object)} viewer={(props.viewer: Object)} />
            {showMoreOptionsBadge && (
                <MoreOptionsBadge
                    localizedPdpUrl={pdpUrl}
                    hasTopMargin={props.moreOptionsTopMargin}
                />
            )}
        </React.Fragment>
    );
}

InfoComponent.defaultProps = {
    currency: 'USD',
    disableLinks: false,
    showInternalSellerName: false,
    showByLine: true,
    showMoreOptionsBadge: false,
    showSellerName: true,
    showCreatorName: false,
    showPrice: true,
    showTitle: true,
    priceBold: false,
    titleOneLine: false,
};

export const Info = createFragmentContainer(InfoComponent, {
    viewer: graphql`
        fragment Info_viewer on Viewer
            @argumentDefinitions(
                fetchRegionalInfo: { type: "Boolean", defaultValue: false }
                userZipCode: { type: "String", defaultValue: "0" }
                userCountryCode: { type: "String", defaultValue: "" }
            ) {
            ...Shipping_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
        }
    `,
    item: graphql`
        fragment Info_item on Item
            @argumentDefinitions(
                isTrade: { type: "Boolean", defaultValue: false }
                pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
                showPrice: { type: "Boolean", defaultValue: true }
            ) {
            ...Price_item
                @arguments(
                    pageDisplayEnum: $pageDisplayEnum
                    isTrade: $isTrade
                    showPrice: $showPrice
                )
            ...Shipping_item
            classification {
                creationDate
            }
            creators {
                attribution
                creator {
                    displayName
                }
            }
            localizedPdpUrl
            title
            vertical
        }
    `,
    seller: graphql`
        fragment Info_seller on Seller {
            sellerProfile {
                company
            }
        }
    `,
});
